import { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  Users,
  LogOut,
  Menu,
  X,
  Plus,
  Edit,
  Trash2,
  ExternalLink,
  Webhook,
  Copy,
  RefreshCw,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../lib/api';
const SITE_LOGO = "https://customer-assets.emergentagent.com/job_9dd50a71-7bb8-45b9-bdec-566056107363/artifacts/kgdjrixe_Untitled%20design.png";

// API helper with auth
const authApi = axios.create({
  baseURL: API
});

authApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Dashboard Overview
const DashboardOverview = () => {
  const [stats, setStats] = useState({ events: 0, sponsors: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [eventsRes, sponsorsRes] = await Promise.all([
          authApi.get('/admin/events'),
          authApi.get('/admin/sponsors')
        ]);
        setStats({
          events: eventsRes.data.length,
          sponsors: sponsorsRes.data.length
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    fetchStats();
  }, []);

  return (
    <div data-testid="admin-dashboard-overview">
      <h1 className="font-outfit text-3xl font-bold text-white mb-8">Dashboard</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-purple-500" />
            </div>
            <div>
              <p className="text-zinc-500 text-sm">Total Events</p>
              <p className="text-3xl font-bold text-white">{stats.events}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-purple-500" />
            </div>
            <div>
              <p className="text-zinc-500 text-sm">Total Sponsors</p>
              <p className="text-3xl font-bold text-white">{stats.sponsors}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h2 className="font-outfit text-xl font-semibold text-white mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-4">
          <Link 
            to="/admin/events" 
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <Plus size={18} />
            Add Event
          </Link>
          <Link 
            to="/admin/sponsors" 
            className="bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <Plus size={18} />
            Add Sponsor
          </Link>
        </div>
      </div>
    </div>
  );
};

// Events Management
const EventsManagement = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    event_date: '',
    event_time: '',
    venue_name: '',
    venue_address: '',
    city: 'Baltimore',
    state: 'Maryland',
    zip_code: '',
    ticket_url: '',
    is_featured: false,
    is_active: true
  });

  const fetchEvents = async () => {
    try {
      const response = await authApi.get('/admin/events');
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await authApi.put(`/admin/events/${editingEvent.id}`, formData);
        toast.success('Event updated successfully');
      } else {
        await authApi.post('/admin/events', formData);
        toast.success('Event created successfully');
      }
      setShowForm(false);
      setEditingEvent(null);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save event');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await authApi.delete(`/admin/events/${id}`);
      toast.success('Event deleted');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      image_url: event.image_url,
      event_date: event.event_date,
      event_time: event.event_time || '',
      venue_name: event.venue_name,
      venue_address: event.venue_address,
      city: event.city,
      state: event.state,
      zip_code: event.zip_code || '',
      ticket_url: event.ticket_url || '',
      is_featured: event.is_featured,
      is_active: event.is_active
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      image_url: '',
      event_date: '',
      event_time: '',
      venue_name: '',
      venue_address: '',
      city: 'Baltimore',
      state: 'Maryland',
      zip_code: '',
      ticket_url: '',
      is_featured: false,
      is_active: true
    });
  };

  return (
    <div data-testid="admin-events-management">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-outfit text-3xl font-bold text-white">Events</h1>
        <button
          onClick={() => { setShowForm(true); setEditingEvent(null); resetForm(); }}
          data-testid="add-event-btn"
          className="bg-rose-500 hover:bg-rose-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
        >
          <Plus size={18} />
          Add Event
        </button>
      </div>

      {showForm && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-8">
          <h2 className="font-outfit text-xl font-semibold text-white mb-6">
            {editingEvent ? 'Edit Event' : 'Add New Event'}
          </h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm text-zinc-400 mb-1">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
                data-testid="event-title-input"
                className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-zinc-400 mb-1">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                required
                rows={3}
                className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-zinc-400 mb-1">Image URL *</label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                required
                className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Event Date *</label>
              <input
                type="date"
                value={formData.event_date}
                onChange={(e) => setFormData({...formData, event_date: e.target.value})}
                required
                className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Event Time</label>
              <input
                type="text"
                value={formData.event_time}
                onChange={(e) => setFormData({...formData, event_time: e.target.value})}
                placeholder="e.g., 7:00 PM"
                className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Venue Name *</label>
              <input
                type="text"
                value={formData.venue_name}
                onChange={(e) => setFormData({...formData, venue_name: e.target.value})}
                required
                className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Venue Address *</label>
              <input
                type="text"
                value={formData.venue_address}
                onChange={(e) => setFormData({...formData, venue_address: e.target.value})}
                required
                className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">City *</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                required
                className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">State</label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({...formData, state: e.target.value})}
                className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Ticket URL</label>
              <input
                type="url"
                value={formData.ticket_url}
                onChange={(e) => setFormData({...formData, ticket_url: e.target.value})}
                className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500"
              />
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                  className="w-4 h-4 rounded border-zinc-600 bg-black/50 text-purple-500 focus:ring-purple-500"
                />
                <span className="text-zinc-400">Featured</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="w-4 h-4 rounded border-zinc-600 bg-black/50 text-purple-500 focus:ring-purple-500"
                />
                <span className="text-zinc-400">Active</span>
              </label>
            </div>
            <div className="md:col-span-2 flex gap-4 mt-4">
              <button
                type="submit"
                data-testid="save-event-btn"
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                {editingEvent ? 'Update Event' : 'Create Event'}
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); setEditingEvent(null); resetForm(); }}
                className="bg-zinc-800 hover:bg-zinc-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-zinc-500">Loading events...</div>
      ) : events.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">No events found. Create your first event!</div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-zinc-800">
              <tr>
                <th className="text-left px-4 py-3 text-zinc-400 font-medium">Event</th>
                <th className="text-left px-4 py-3 text-zinc-400 font-medium">Date</th>
                <th className="text-left px-4 py-3 text-zinc-400 font-medium">Venue</th>
                <th className="text-left px-4 py-3 text-zinc-400 font-medium">Status</th>
                <th className="text-right px-4 py-3 text-zinc-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id} className="border-t border-zinc-800 hover:bg-zinc-800/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img 
                        src={event.image_url} 
                        alt={event.title}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                      <div>
                        <p className="text-white font-medium">{event.title}</p>
                        <p className="text-zinc-500 text-sm">
                          {event.ingest_method === 'sync'
                            ? `Synced from ${event.source}`
                            : event.ingest_method === 'webhook'
                              ? `Webhook from ${event.source}`
                              : 'Manual'}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-400">{event.event_date}</td>
                  <td className="px-4 py-3 text-zinc-400">{event.venue_name}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {event.is_featured && (
                        <span className="bg-yellow-500/20 text-yellow-500 px-2 py-0.5 rounded text-xs">Featured</span>
                      )}
                      <span className={`px-2 py-0.5 rounded text-xs ${event.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                        {event.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(event)}
                        className="p-2 text-zinc-400 hover:text-white transition-colors"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(event.id)}
                        className="p-2 text-zinc-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Sponsors Management
const SponsorsManagement = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    logo_url: '',
    website_url: '',
    is_active: true,
    order: 0
  });

  const fetchSponsors = async () => {
    try {
      const response = await authApi.get('/admin/sponsors');
      setSponsors(response.data);
    } catch (error) {
      toast.error('Failed to fetch sponsors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSponsors();
  }, []);

  const handleLogoUpload = async (file) => {
    if (!file) return;
    const data = new FormData();
    data.append('file', file);
    setUploadingLogo(true);
    try {
      const response = await authApi.post('/admin/sponsors/upload-logo', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData((prev) => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSponsor) {
        await authApi.put(`/admin/sponsors/${editingSponsor.id}`, formData);
        toast.success('Sponsor updated successfully');
      } else {
        await authApi.post('/admin/sponsors', formData);
        toast.success('Sponsor created successfully');
      }
      setShowForm(false);
      setEditingSponsor(null);
      resetForm();
      fetchSponsors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save sponsor');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this sponsor?')) return;
    try {
      await authApi.delete(`/admin/sponsors/${id}`);
      toast.success('Sponsor deleted');
      fetchSponsors();
    } catch (error) {
      toast.error('Failed to delete sponsor');
    }
  };

  const handleEdit = (sponsor) => {
    setEditingSponsor(sponsor);
    setFormData({
      name: sponsor.name,
      logo_url: sponsor.logo_url,
      website_url: sponsor.website_url,
      is_active: sponsor.is_active,
      order: sponsor.order
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      logo_url: '',
      website_url: '',
      is_active: true,
      order: 0
    });
  };

  return (
    <div data-testid="admin-sponsors-management">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-outfit text-3xl font-bold text-white">Sponsors</h1>
        <button
          onClick={() => { setShowForm(true); setEditingSponsor(null); resetForm(); }}
          data-testid="add-sponsor-btn"
          className="bg-rose-500 hover:bg-rose-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
        >
          <Plus size={18} />
          Add Sponsor
        </button>
      </div>

      {showForm && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-8">
          <h2 className="font-outfit text-xl font-semibold text-white mb-6">
            {editingSponsor ? 'Edit Sponsor' : 'Add New Sponsor'}
          </h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                data-testid="sponsor-name-input"
                className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Display Order</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Logo URL *</label>
              <input
                type="text"
                value={formData.logo_url}
                onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
                required
                placeholder="https://example.com/logo.png or /api/sponsor-logos/your-file.png"
                className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500"
              />
              <p className="text-xs text-zinc-500 mt-1">Paste a full URL or upload a logo file below. Uploaded logos use app-relative paths.</p>
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Website URL *</label>
              <input
                type="url"
                value={formData.website_url}
                onChange={(e) => setFormData({...formData, website_url: e.target.value})}
                required
                className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-zinc-400 mb-1">Upload Logo</label>
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500 file:mr-4 file:rounded-md file:border-0 file:bg-purple-600 file:px-3 file:py-2 file:text-white hover:file:bg-purple-700"
              />
              <p className="text-xs text-zinc-500 mt-1">
                {uploadingLogo ? 'Uploading logo…' : 'Upload a PNG, JPG, WEBP, or SVG up to 10MB. The uploaded file URL will fill automatically.'}
              </p>
            </div>
            <div className="flex items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="w-4 h-4 rounded border-zinc-600 bg-black/50 text-purple-500 focus:ring-purple-500"
                />
                <span className="text-zinc-400">Active</span>
              </label>
            </div>
            <div className="md:col-span-2 flex gap-4 mt-4">
              <button
                type="submit"
                data-testid="save-sponsor-btn"
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                {editingSponsor ? 'Update Sponsor' : 'Create Sponsor'}
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); setEditingSponsor(null); resetForm(); }}
                className="bg-zinc-800 hover:bg-zinc-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-zinc-500">Loading sponsors...</div>
      ) : sponsors.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">No sponsors found. Add your first sponsor!</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sponsors.map((sponsor) => (
            <div key={sponsor.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <img 
                  src={sponsor.logo_url}
                  alt={sponsor.name}
                  className="h-12 w-auto object-contain sponsor-logo"
                />
                <span className={`px-2 py-0.5 rounded text-xs ${sponsor.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                  {sponsor.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <h3 className="text-white font-semibold mb-2">{sponsor.name}</h3>
              <a 
                href={sponsor.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-500 text-sm hover:text-rose-500 transition-colors flex items-center gap-1"
              >
                {sponsor.website_url}
                <ExternalLink size={12} />
              </a>
              <p className="text-zinc-600 text-sm mt-2">Order: {sponsor.order}</p>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => handleEdit(sponsor)}
                  className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1"
                >
                  <Edit size={14} />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(sponsor.id)}
                  className="bg-zinc-800 hover:bg-red-500/20 hover:text-red-500 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PremiumEventsManagement = () => {
  const emptyForm = { headline: '', sub_headline: '', description: '', event_location: '', event_date: '', event_time: '', event_end_time: '', ticket_url: '', image_url: '', is_active: true, order: 0 };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  const fetchItems = async () => {
    try {
      const response = await authApi.get('/admin/premium-events');
      setItems(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch premium events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const resetForm = () => setFormData(emptyForm);

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      headline: item.headline || '',
      sub_headline: item.sub_headline || '',
      description: item.description || '',
      event_location: item.event_location || '',
      event_date: item.event_date || '',
      event_time: item.event_time || '',
      event_end_time: item.event_end_time || '',
      ticket_url: item.ticket_url || '',
      image_url: item.image_url || '',
      is_active: item.is_active,
      order: item.order || 0,
    });
    setShowForm(true);
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    const data = new FormData();
    data.append('file', file);
    setUploadingImage(true);
    try {
      const response = await authApi.post('/admin/premium-events/upload-image', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFormData((prev) => ({ ...prev, image_url: response.data.image_url }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData, order: parseInt(formData.order, 10) || 0 };
    try {
      if (editingItem) {
        await authApi.put(`/admin/premium-events/${editingItem.id}`, payload);
        toast.success('Premium event updated');
      } else {
        await authApi.post('/admin/premium-events', payload);
        toast.success('Premium event created');
      }
      setShowForm(false);
      setEditingItem(null);
      resetForm();
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save premium event');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this premium event?')) return;
    try {
      await authApi.delete(`/admin/premium-events/${id}`);
      toast.success('Premium event deleted');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete premium event');
    }
  };

  return (
    <div data-testid="admin-premium-events-management">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-white">Premium Events</h1>
          <p className="text-zinc-400 mt-2">Configure the optional homepage section shown directly under the hero. Multiple active items rotate every 3 seconds.</p>
        </div>
        <button onClick={() => { setShowForm(true); setEditingItem(null); resetForm(); }} className="bg-rose-500 hover:bg-rose-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2">
          <Plus size={18} /> Add Premium Event
        </button>
      </div>

      {showForm && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-8">
          <h2 className="font-outfit text-xl font-semibold text-white mb-6">{editingItem ? 'Edit Premium Event' : 'Add Premium Event'}</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Headline</label>
              <input type="text" value={formData.headline} onChange={(e) => setFormData({ ...formData, headline: e.target.value })} className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500" />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Sub headline</label>
              <input type="text" value={formData.sub_headline} onChange={(e) => setFormData({ ...formData, sub_headline: e.target.value })} className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-zinc-400 mb-1">Description</label>
              <textarea rows={3} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500" />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Location</label>
              <input type="text" value={formData.event_location} onChange={(e) => setFormData({ ...formData, event_location: e.target.value })} placeholder="Venue or location" className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500" />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Date</label>
              <input type="date" value={formData.event_date} onChange={(e) => setFormData({ ...formData, event_date: e.target.value })} className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500" />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Start Time</label>
              <input type="text" value={formData.event_time} onChange={(e) => setFormData({ ...formData, event_time: e.target.value })} placeholder="e.g., 7:00 PM" className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500" />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">End Time</label>
              <input type="text" value={formData.event_end_time} onChange={(e) => setFormData({ ...formData, event_end_time: e.target.value })} placeholder="e.g., 10:30 PM" className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500" />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Ticket URL</label>
              <input type="url" value={formData.ticket_url} onChange={(e) => setFormData({ ...formData, ticket_url: e.target.value })} className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500" />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Display order</label>
              <input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: e.target.value })} className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-zinc-400 mb-1">Image URL</label>
              <input type="text" value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder="Paste a URL or upload below" className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-zinc-400 mb-1">Upload image</label>
              <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e.target.files?.[0])} className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500 file:mr-4 file:rounded-md file:border-0 file:bg-purple-600 file:px-3 file:py-2 file:text-white hover:file:bg-purple-700" />
              <p className="text-xs text-zinc-500 mt-1">{uploadingImage ? 'Uploading image…' : 'Optional. Uploaded image URL fills automatically.'}</p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="w-4 h-4 rounded border-zinc-600 bg-black/50 text-purple-500 focus:ring-purple-500" />
              <span className="text-zinc-400">Enabled on homepage</span>
            </label>
            <div className="md:col-span-2 flex gap-4 mt-4">
              <button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium transition-colors">{editingItem ? 'Update Premium Event' : 'Create Premium Event'}</button>
              <button type="button" onClick={() => { setShowForm(false); setEditingItem(null); resetForm(); }} className="bg-zinc-800 hover:bg-zinc-700 text-white px-6 py-2 rounded-lg font-medium transition-colors">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <div className="text-center py-12 text-zinc-500">Loading premium events...</div> : items.length === 0 ? <div className="text-center py-12 text-zinc-500">No premium events configured.</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {items.map((item) => (
            <div key={item.id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              {item.image_url && <img src={item.image_url} alt={item.headline || 'Premium event'} className="w-full h-44 object-cover" />}
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className={`px-2 py-0.5 rounded text-xs ${item.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>{item.is_active ? 'Enabled' : 'Disabled'}</span>
                  <span className="text-zinc-600 text-sm">Order: {item.order}</span>
                </div>
                {item.headline && <h3 className="text-white font-semibold text-lg mb-1">{item.headline}</h3>}
                {item.sub_headline && <p className="text-purple-300 text-sm mb-2">{item.sub_headline}</p>}
                {(item.event_date || item.event_time || item.event_end_time || item.event_location) && <p className="text-rose-300 text-sm mb-2">{[item.event_date, [item.event_time, item.event_end_time].filter(Boolean).join(' – '), item.event_location].filter(Boolean).join(' • ')}</p>}
                {item.description && <p className="text-zinc-400 text-sm line-clamp-3">{item.description}</p>}
                <div className="flex gap-2 mt-4">
                  <button onClick={() => handleEdit(item)} className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1"><Edit size={14} /> Edit</button>
                  <button onClick={() => handleDelete(item.id)} className="bg-zinc-800 hover:bg-red-500/20 hover:text-red-500 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const EventWebhookSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [config, setConfig] = useState(null);
  const [lastSyncResult, setLastSyncResult] = useState(null);
  const [formData, setFormData] = useState({
    enabled: false,
    source_name: 'Event webhook',
    notes: '',
    secret_token: '',
    regenerate_secret: false,
    sync_enabled: false,
    sync_source_url: '',
    sync_auth_header: 'Authorization',
    sync_auth_token: '',
    clear_sync_auth_token: false,
    sync_notes: '',
  });

  const fetchConfig = async () => {
    try {
      const response = await authApi.get('/admin/event-webhook');
      setConfig(response.data);
      setFormData({
        enabled: response.data.enabled,
        source_name: response.data.source_name,
        notes: response.data.notes || '',
        secret_token: '',
        regenerate_secret: false,
        sync_enabled: response.data.sync_enabled,
        sync_source_url: response.data.sync_source_url || '',
        sync_auth_header: response.data.sync_auth_header || 'Authorization',
        sync_auth_token: '',
        clear_sync_auth_token: false,
        sync_notes: response.data.sync_notes || '',
      });
    } catch (error) {
      toast.error('Failed to fetch webhook settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const webhookUrl = config ? `${window.location.origin}${config.endpoint_path}` : '';

  const copyToClipboard = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch (error) {
      toast.error(`Failed to copy ${label.toLowerCase()}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        enabled: formData.enabled,
        source_name: formData.source_name,
        notes: formData.notes,
        regenerate_secret: formData.regenerate_secret,
        sync_enabled: formData.sync_enabled,
        sync_source_url: formData.sync_source_url,
        sync_auth_header: formData.sync_auth_header,
        clear_sync_auth_token: formData.clear_sync_auth_token,
        sync_notes: formData.sync_notes,
      };

      if (formData.secret_token.trim()) payload.secret_token = formData.secret_token.trim();
      if (formData.sync_auth_token.trim()) payload.sync_auth_token = formData.sync_auth_token.trim();

      const response = await authApi.put('/admin/event-webhook', payload);
      setConfig(response.data);
      setFormData((current) => ({
        ...current,
        secret_token: '',
        regenerate_secret: false,
        sync_auth_token: '',
        clear_sync_auth_token: false,
      }));
      toast.success('Webhook settings updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update webhook settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const response = await authApi.post('/admin/event-webhook/sync');
      setLastSyncResult(response.data);
      setConfig((current) => current ? { ...current, last_sync_at: response.data.synced_at } : current);
      toast.success(`Sync complete: ${response.data.created} created, ${response.data.updated} updated`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run sync');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-zinc-500">Loading webhook settings...</div>;
  }

  return (
    <div data-testid="admin-event-webhook-settings" className="space-y-6">
      <div>
        <h1 className="font-outfit text-3xl font-bold text-white mb-2">Event Webhook + Sync</h1>
        <p className="text-zinc-400 max-w-3xl">
          Use the webhook for future pushes and the sync source to backfill current events now. Both flows upsert by
          external_id so they can safely coexist.
        </p>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-col md:flex-row">
          <div>
            <p className="text-sm text-zinc-500 mb-1">Webhook endpoint</p>
            <p className="text-white font-mono break-all">{webhookUrl}</p>
          </div>
          <button type="button" onClick={() => copyToClipboard(webhookUrl, 'Webhook URL')} className="bg-zinc-800 hover:bg-zinc-700 text-white px-3 py-2 rounded-lg text-sm flex items-center gap-2">
            <Copy size={16} /> Copy URL
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="bg-black/30 border border-zinc-800 rounded-lg p-4">
            <p className="text-zinc-500 mb-2">Webhook auth</p>
            <code className="text-zinc-200 break-all">X-Webhook-Secret: your-secret</code>
          </div>
          <div className="bg-black/30 border border-zinc-800 rounded-lg p-4">
            <p className="text-zinc-500 mb-2">Sync response shape</p>
            <code className="text-zinc-200 break-all">[{`{ external_id, title, ... }`}] or {`{ events: [...] }`}</code>
          </div>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
        <div className="flex items-center justify-between gap-4 flex-col md:flex-row">
          <div>
            <h2 className="text-white font-semibold text-xl">Sync now</h2>
            <p className="text-zinc-500 text-sm">Pull current events from the configured upstream source immediately.</p>
            {config?.last_sync_at && <p className="text-zinc-400 text-sm mt-1">Last sync: {new Date(config.last_sync_at).toLocaleString()}</p>}
          </div>
          <button type="button" disabled={syncing || !formData.sync_enabled || !formData.sync_source_url.trim()} onClick={handleSyncNow} className="bg-rose-500 hover:bg-rose-600 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2">
            <RefreshCw size={18} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Syncing...' : 'Run Sync Now'}
          </button>
        </div>
        {lastSyncResult && (
          <div className="bg-black/30 border border-zinc-800 rounded-lg p-4 text-sm text-zinc-300">
            <p>Received: {lastSyncResult.total_received}</p>
            <p>Created: {lastSyncResult.created}</p>
            <p>Updated: {lastSyncResult.updated}</p>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-6">
        <div className="flex items-center gap-3">
          <input type="checkbox" checked={formData.enabled} onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })} className="w-4 h-4 rounded border-zinc-600 bg-black/50 text-purple-500 focus:ring-purple-500" />
          <div>
            <p className="text-white font-medium">Enable event webhook</p>
            <p className="text-zinc-500 text-sm">When disabled, the webhook endpoint rejects requests.</p>
          </div>
        </div>

        <div>
          <label className="block text-sm text-zinc-400 mb-1">Source name</label>
          <input type="text" value={formData.source_name} onChange={(e) => setFormData({ ...formData, source_name: e.target.value })} required className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500" />
          <p className="text-zinc-500 text-xs mt-1">Used on events imported by either webhook or sync.</p>
        </div>

        <div>
          <label className="block text-sm text-zinc-400 mb-1">Current webhook secret</label>
          <input type="text" value={config?.secret_token_masked || ''} disabled className="w-full bg-black/30 border border-zinc-800 text-zinc-300 px-4 py-2 rounded-lg" />
        </div>

        <div>
          <label className="block text-sm text-zinc-400 mb-1">Set new webhook secret</label>
          <input type="text" value={formData.secret_token} onChange={(e) => setFormData({ ...formData, secret_token: e.target.value, regenerate_secret: false })} placeholder="Leave blank to keep the existing secret" className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500" />
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={formData.regenerate_secret} onChange={(e) => setFormData({ ...formData, regenerate_secret: e.target.checked, secret_token: '' })} className="w-4 h-4 rounded border-zinc-600 bg-black/50 text-purple-500 focus:ring-purple-500" />
          <span className="text-zinc-400 flex items-center gap-2"><RefreshCw size={16} /> Generate a new random webhook secret on save</span>
        </label>

        <div className="border-t border-zinc-800 pt-6 space-y-4">
          <div className="flex items-center gap-3">
            <input type="checkbox" checked={formData.sync_enabled} onChange={(e) => setFormData({ ...formData, sync_enabled: e.target.checked })} className="w-4 h-4 rounded border-zinc-600 bg-black/50 text-purple-500 focus:ring-purple-500" />
            <div>
              <p className="text-white font-medium">Enable sync/backfill source</p>
              <p className="text-zinc-500 text-sm">Allows admins to pull current events from an upstream JSON endpoint.</p>
            </div>
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-1">Sync source URL</label>
            <input type="url" value={formData.sync_source_url} onChange={(e) => setFormData({ ...formData, sync_source_url: e.target.value })} placeholder="https://upstream.example.com/events" className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500" />
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-1">Sync auth header</label>
            <input type="text" value={formData.sync_auth_header} onChange={(e) => setFormData({ ...formData, sync_auth_header: e.target.value })} className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500" />
            <p className="text-zinc-500 text-xs mt-1">Use Authorization for bearer auth, or another header name if the upstream expects one.</p>
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-1">Set new sync auth token</label>
            <input type="text" value={formData.sync_auth_token} onChange={(e) => setFormData({ ...formData, sync_auth_token: e.target.value, clear_sync_auth_token: false })} placeholder="Leave blank to keep the existing token" className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500" />
            {config?.has_sync_auth_token && <p className="text-zinc-500 text-xs mt-1">Stored token: {config.sync_auth_token_masked}</p>}
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={formData.clear_sync_auth_token} onChange={(e) => setFormData({ ...formData, clear_sync_auth_token: e.target.checked, sync_auth_token: '' })} className="w-4 h-4 rounded border-zinc-600 bg-black/50 text-purple-500 focus:ring-purple-500" />
            <span className="text-zinc-400">Clear saved sync auth token on save</span>
          </label>

          <div>
            <label className="block text-sm text-zinc-400 mb-1">Notes</label>
            <textarea value={formData.sync_notes} onChange={(e) => setFormData({ ...formData, sync_notes: e.target.value })} rows={3} placeholder="Anything useful about the upstream sync source." className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500" />
          </div>
        </div>

        <div>
          <label className="block text-sm text-zinc-400 mb-1">General notes</label>
          <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={4} placeholder="Optional: owner, operational notes, payload quirks, etc." className="w-full bg-black/50 border border-zinc-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-purple-500" />
        </div>

        <div className="flex gap-4">
          <button type="submit" disabled={saving} className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-6 py-2 rounded-lg font-medium transition-colors">
            {saving ? 'Saving...' : 'Save Integration Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Main Admin Dashboard
const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/events', label: 'Events', icon: Calendar },
    { path: '/admin/premium-events', label: 'Premium Events', icon: Star },
    { path: '/admin/sponsors', label: 'Sponsors', icon: Users },
    { path: '/admin/webhook', label: 'Webhook', icon: Webhook },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-zinc-900 border-r border-zinc-800 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-300`}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-zinc-800">
            <Link to="/" className="flex items-center gap-2">
              <img src={SITE_LOGO} alt="Logo" className="h-10" />
            </Link>
            <p className="text-zinc-500 text-sm mt-2">Admin Panel</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'bg-purple-500/10 text-purple-500'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon size={20} />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-zinc-800">
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-3 px-4 py-3 w-full text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
            >
              <LogOut size={20} />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 min-h-screen">
        {/* Mobile header */}
        <header className="lg:hidden bg-zinc-900 border-b border-zinc-800 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-white p-2"
          >
            <Menu size={24} />
          </button>
          <img src={SITE_LOGO} alt="Logo" className="h-8" />
          <div className="w-10" />
        </header>

        <div className="p-6 lg:p-8">
          <Routes>
            <Route path="/dashboard" element={<DashboardOverview />} />
            <Route path="/events" element={<EventsManagement />} />
            <Route path="/premium-events" element={<PremiumEventsManagement />} />
            <Route path="/sponsors" element={<SponsorsManagement />} />
            <Route path="/webhook" element={<EventWebhookSettings />} />
            <Route path="*" element={<DashboardOverview />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
